/* eslint-disable */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";

import { useTranslation } from "react-i18next";
import MyMaorLayout from "../../templates/MyMaorLayout";
import SearchBar from "../../atoms/SearchBar";
import Loader from "../../atoms/Loader";
import Modal from "../../organisms/Modal";
import Preview from "../../organisms/ColoringProjectList/components/Preview";
import PlaylistModal from "../../organisms/Playlist/PlaylistModal";
import useMyMaor from "../MyMaor/useMyMaor";
import useVideo from "../Library/components/Project/useVideo";
import useLibraryFilter from "../Library/useLibraryFilter";
import LibraryFilterClear from "../../molecules/LibraryFilterClear";
import PillButton, { VARIANTS as PILL_VARIANTS } from "../../atoms/PillButton";
import { PROJECTS } from "../../../constants";
import LibraryAPI from "../../../api/library";
import placeholder from "../../../assets/images/video-thumbnail-placeholder.png";
import classes from "./ColoringPages.module.scss";

const libraryAPI = new LibraryAPI();

const themeColors = ["green", "blue", "pink", "orange", "purple", "yellow", "gray"];

const ColoringPages = observer(() => {
  const { t } = useTranslation();
  const { searching, search, sorting } = useMyMaor();
  const { filterData, appliedFilters } = useLibraryFilter();
  const searchBarRef = useRef(null);
  const video = useVideo();
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(null);

  const fetchColoringPages = useCallback(async (searchVal, holidayId, pageNum = 1) => {
    setIsLoading(true);
    try {
      const res = await libraryAPI.get({
        page: pageNum,
        project: "my-maor",
        search: searchVal || "",
        order: "new",
        videoType: "",
        tags: [],
        holidayId: holidayId || 0,
        limitRecord: 48,
        coloringPages: true,
        upcomingHoliday: false,
      });
      const response = await res.json();
      const fetchedItems = response?.data?.items || response?.items || [];
      const nextPage = response?.data?.next_page !== undefined ? response?.data?.next_page : response?.next_page;

      if (pageNum === 1) {
        setItems(fetchedItems);
      } else {
        setItems((prev) => [...prev, ...fetchedItems]);
      }
      setHasNextPage(nextPage);
      setPage(pageNum);
    } catch (err) {
      console.error("Coloring pages load error:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    filterData.holidaysToday.onLoad();
  }, []);

  useEffect(() => {
    fetchColoringPages(search.value, selectedHoliday?.value, 1);
  }, [search.value, selectedHoliday]);

  const onScrollToSection = () => {
    const el = document.querySelector("#coloringSection");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  let holidayOptions = [];
  if (filterData && !filterData.holidaysToday.isLoading && !filterData.holidaysToday.isError) {
    holidayOptions = (filterData.holidaysToday.data || []).map((h) => ({
      id: `holiday-${h.id}`,
      label: h.name_english,
      value: h.id,
    }));
  }

  const handleHolidayPillClick = (option) => {
    if (selectedHoliday?.value === option.value) {
      setSelectedHoliday(null);
      appliedFilters.onClear("holiday");
    } else {
      setSelectedHoliday(option);
      appliedFilters.add("holiday", option.label, { paramValue: option.value });
      onScrollToSection();
    }
  };

  return (
    <MyMaorLayout>
      <div className={classes.container}>
        <h1 className={classes.pageTitle}>Coloring Pages</h1>

        <div className={classes.newSearchWrap}>
          <div className={classes.searchRow}>
            <SearchBar
              ref={searchBarRef}
              id="async-autocomplete-search"
              placeholder="Search by topic"
              value={search.value}
              page="mymaor"
              disableFloating
              onSearch={searching.onSearch}
              searchingOptions={searching.options}
              isSearching={searching.isSearching}
              onChange={(value) => {
                onScrollToSection();
                search.onChange(value);
              }}
            >
              <img
                src="/email_images/drv/assests/yellow-search.png"
                alt="search"
                onClick={() => {
                  const searchValue = searchBarRef.current?.getInputValue();
                  if (searchValue && searchValue !== "") {
                    onScrollToSection();
                    search.onChange(searchValue);
                  }
                }}
              />
            </SearchBar>
          </div>

          {holidayOptions.length > 0 && (
            <div className={classes.holidayPillsRow}>
              {holidayOptions.map((option) => (
                <PillButton
                  key={option.id}
                  variant={PILL_VARIANTS.YELLOW}
                  onClick={() => handleHolidayPillClick(option)}
                  style={{ minWidth: "auto", fontSize: "15px", padding: "8px 24px" }}
                >
                  {option.label}
                </PillButton>
              ))}
            </div>
          )}
        </div>

        {Object.keys(appliedFilters.data).length !== 0 && (
          <div className={classes.filterClearWrapper}>
            <LibraryFilterClear
              filters={appliedFilters.data}
              onClear={(key) => {
                if (key === "holiday") {
                  setSelectedHoliday(null);
                }
                appliedFilters.onClear(key);
              }}
            />
          </div>
        )}

        <div id="coloringSection" />

        {isLoading && items.length === 0 ? (
          <Loader />
        ) : (
          <>
            <ul className={classes.grid}>
              {items.map((item, index) => (
                <li key={item.id + "_" + index} className={classes.gridItem}>
                  <Preview
                    {...item}
                    projectType={PROJECTS.MY_MAOR.ID}
                    internal_id={item.id}
                    placeholder={placeholder}
                    onSelect={video.onOpen}
                    showAllList={false}
                    themeColor={themeColors[index % themeColors.length]}
                  />
                </li>
              ))}
            </ul>
            {hasNextPage && (
              <div className={classes.footer}>
                {isLoading ? (
                  <Loader />
                ) : (
                  <button
                    className={classes.button}
                    onClick={() => fetchColoringPages(search.value, selectedHoliday?.value, hasNextPage)}
                  >
                    {t("pages.library.labels.1")}
                  </button>
                )}
              </div>
            )}
            {items.length === 0 && !isLoading && (
              <p className={classes.empty}>No coloring pages found.</p>
            )}
          </>
        )}

        <Modal hasPlaylist={true} opened={video.isOpened}>
          <PlaylistModal video={video} />
        </Modal>
      </div>
    </MyMaorLayout>
  );
});

export default ColoringPages;
